/**
 * Defaults nobody chose, named before they are sent — SPEC §2.1, §4.4.
 *
 * From the first week of the soft launch, and from two instructors who both did nothing
 * wrong.
 *
 * James marked seven days and every one took his home location, because he never saw that
 * there was a choice: he teaches at five sites, the picker defaults to home, and nothing
 * asked. The coverage grid then placed him at the wrong site, which is the number Luke plans
 * classes from.
 *
 * Madeline left the time alone, so her days went in as "any time". §2.1 says an exact time
 * is strictly better, and on the grid "Saturday, any time" is indistinguishable from a real
 * all-day availability.
 *
 * ## It never blocks
 *
 * "All at one site" and "no particular time" are both legitimate answers, and refusing them
 * would be worse than the problem. This only makes the default visible at the moment the
 * whole month is in view, which is the one moment somebody is looking at all of it at once.
 *
 * ## One component, used twice
 *
 * Both instructors were FIRST-TIME users, so both submitted from the onboarding wizard,
 * which has its own submit button and never reaches the month screen's confirmation. A
 * review that lived only on the month screen would have missed the exact two people who
 * prompted it. This renders in both.
 */

import type { LocationOption, Occurrence } from '../lib/api.js';

export function DefaultsReview({
  occurrences,
  locations,
  subjectName,
}: {
  occurrences: readonly Occurrence[];
  locations: readonly LocationOption[];
  /** Set when an admin is entering for somebody else, so the copy is not second person. */
  subjectName?: string;
}) {
  if (occurrences.length === 0) return null;

  // A day carrying AM or PM has said something real about when. Only ALL_DAY with no time
  // is the untouched default.
  const noTime = occurrences.filter((o) => !o.startTime && o.period === 'ALL_DAY');

  const used = new Set(occurrences.map((o) => o.locationId));
  const others = locations.length > 1 && used.size === 1
    ? locations.filter((l) => !used.has(l.id))
    : [];
  const sole = others.length > 0 ? locations.find((l) => used.has(l.id)) : undefined;

  if (noTime.length === 0 && others.length === 0) return null;

  const they = subjectName ? `${subjectName} also teaches` : 'You also teach';

  return (
    <div className="notice notice--warn" style={{ alignItems: 'flex-start' }}>
      <span className="notice__icon" aria-hidden="true">!</span>
      <span className="grow">
        <strong>Worth a quick check</strong>
        <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
          {others.length > 0 && (
            <li className="small">
              Every day is at <strong>{sole?.canonicalName}</strong>. {they} at{' '}
              {others.map((l) => l.canonicalName).join(', ')}.
            </li>
          )}
          {noTime.length > 0 && (
            <li className="small">
              {noTime.length} day{noTime.length === 1 ? '' : 's'}{' '}
              {noTime.length === 1 ? 'has' : 'have'} no time set, so{' '}
              {noTime.length === 1 ? 'it' : 'they'} will show as any time. An exact time helps
              us build the right class.
            </li>
          )}
        </ul>
      </span>
    </div>
  );
}
